import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { PaymentMethodsEditor } from "@/components/PaymentMethodsEditor";
import { Upload, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

export default function Settings() {
  const { setStorefront } = useAuth();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState(params.get("tab") || "general");
  const focusMethodId = params.get("focus");
  const [sf, setSf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [legal, setLegal] = useState(null);
  const logoRef = useRef();

  useEffect(() => { api.get("/store/settings").then((r) => setSf(r.data)); }, []);
  useEffect(() => { api.get("/legal/acceptance-status").then((r) => setLegal(r.data)).catch(() => {}); }, []);
  if (!sf) return <FullScreenLoader />;

  const set = (k, v) => setSf((s) => ({ ...s, [k]: v }));
  const setNested = (k, sub, v) => setSf((s) => ({ ...s, [k]: { ...s[k], [sub]: v } }));

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const path = await uploadFile(f); set("logo_url", path); toast.success("Logo uploaded");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/store/settings", {
        name: sf.name, logo_url: sf.logo_url, banner_url: sf.banner_url, tagline: sf.tagline,
        contact_email: sf.contact_email, contact_phone: sf.contact_phone,
        payment_methods: sf.payment_methods, fulfillment: sf.fulfillment,
        policies: sf.policies, business_hours: sf.business_hours,
        visitor_settings: sf.visitor_settings, low_stock_threshold: parseInt(sf.low_stock_threshold) || 5,
      });
      setStorefront(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const setPm = (idx, field, val) => {
    const pms = sf.payment_methods.map((p, i) => i === idx ? { ...p, [field]: val } : p);
    set("payment_methods", pms);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Store settings</h1>
          <p className="text-muted-foreground mt-1">drelik.com/store/{sf.slug}</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="fulfillment" data-testid="tab-fulfillment">Fulfillment</TabsTrigger>
          <TabsTrigger value="visibility" data-testid="tab-visibility">Visibility</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">Legal</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card className="p-6 rounded-xl border-border space-y-5">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-secondary overflow-hidden flex items-center justify-center">
                {sf.logo_url ? <img src={fileUrl(sf.logo_url)} alt="" className="h-full w-full object-cover" /> : <span className="text-2xl font-bold text-primary">{sf.name?.[0]}</span>}
              </div>
              <Button variant="outline" onClick={() => logoRef.current?.click()} data-testid="upload-logo-btn"><Upload className="h-4 w-4 mr-1.5" /> Upload logo</Button>
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
            </div>
            <div className="space-y-2"><Label>Business name</Label><Input value={sf.name} onChange={(e) => set("name", e.target.value)} className="h-11" data-testid="settings-name-input" /></div>
            <div className="space-y-2"><Label>Tagline</Label><Input value={sf.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="h-11" placeholder="A short description of your store" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Contact email</Label><Input value={sf.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="h-11" /></div>
              <div className="space-y-2"><Label>Contact phone</Label><Input value={sf.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="h-11" /></div>
            </div>
            <div className="space-y-2"><Label>Business hours</Label><Input value={sf.business_hours || ""} onChange={(e) => set("business_hours", e.target.value)} className="h-11" placeholder="Mon–Fri 9am–5pm" /></div>
            <div className="space-y-2"><Label>Store policies</Label><Textarea value={sf.policies || ""} onChange={(e) => set("policies", e.target.value)} rows={4} /></div>
            <div className="space-y-2"><Label>Low stock threshold</Label><Input type="number" value={sf.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className="h-11 w-32" /></div>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card className="p-6 rounded-xl border-border">
            <PaymentMethodsEditor methods={sf.payment_methods} onChange={(pms) => set("payment_methods", pms)} focusMethodId={activeTab === "payments" ? focusMethodId : null} />
          </Card>
        </TabsContent>

        <TabsContent value="fulfillment" className="mt-4">
          <Card className="p-6 rounded-xl border-border space-y-5">
            {[["shipping", "Shipping", "shipping_fee", "shipping_instructions"], ["delivery", "Local delivery", "delivery_fee", "delivery_instructions"], ["pickup", "Local pickup", null, "pickup_instructions"]].map(([key, label, fee, instr]) => (
              <div key={key} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{label}</span>
                  <Switch checked={sf.fulfillment[key]} onCheckedChange={(v) => setNested("fulfillment", key, v)} data-testid={`fulfillment-toggle-${key}`} />
                </div>
                {sf.fulfillment[key] && (
                  <div className="space-y-3">
                    {fee && <div className="space-y-1"><Label className="text-xs">Fee</Label><Input type="number" step="0.01" value={sf.fulfillment[fee]} onChange={(e) => setNested("fulfillment", fee, parseFloat(e.target.value) || 0)} className="h-10 w-32" /></div>}
                    <Textarea value={sf.fulfillment[instr]} onChange={(e) => setNested("fulfillment", instr, e.target.value)} placeholder="Instructions" rows={2} />
                  </div>
                )}
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="visibility" className="mt-4">
          <Card className="p-6 rounded-xl border-border space-y-4">
            <p className="text-sm text-muted-foreground">Control what public visitors can do on your storefront.</p>
            {[["browse", "Allow visitors to browse products"], ["view_pricing", "Show pricing to visitors"], ["allow_registration", "Allow customer registration"], ["allow_orders", "Allow visitors to place orders"]].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between border border-border rounded-lg p-4">
                <span className="text-sm font-medium">{label}</span>
                <Switch checked={sf.visitor_settings[key]} onCheckedChange={(v) => setNested("visitor_settings", key, v)} data-testid={`visitor-toggle-${key}`} />
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="legal" className="mt-4">
          <Card className="p-6 rounded-xl border-border space-y-4" data-testid="legal-agreements-section">
            <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><h3 className="font-semibold">Legal Agreements</h3></div>
            {!legal ? <p className="text-sm text-muted-foreground">Loading...</p> : (
              <>
                {legal.needs_acceptance ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="new-agreement-pending">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">New Agreement Pending</p>
                      <p className="text-sm text-amber-700 mt-0.5">Updated legal documents require your acceptance. You'll be prompted the next time you open your dashboard.</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div><p className="text-xs text-muted-foreground">Status</p><p className="font-medium text-emerald-600 flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Accepted</p></div>
                    <div><p className="text-xs text-muted-foreground">Accepted version</p><p className="font-medium">v{Math.max(0, ...Object.values(legal.accepted_versions || { x: 0 }))}</p></div>
                    <div><p className="text-xs text-muted-foreground">Acceptance date</p><p className="font-medium">{legal.accepted_at ? new Date(legal.accepted_at).toLocaleDateString() : "—"}</p></div>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/legal/agreement" target="_blank"><Button variant="outline" data-testid="view-agreements-btn">View Current Agreements</Button></Link>
                </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
